import React from "react";
import { css } from "glamor";
import * as R from "ramda";
import { Query } from "react-apollo";
import { NarrativeSummary } from "@arachnys/catalog/_shared/NarrativeSummary";
import Table from "@arachnys/styleguide/Table";
import { DocumentListWithFetch } from "../../container/Documents";
import { withApolloState } from "../../util";
import { formatDatetimeString, formatWithJsonSchema } from "../../formatters";
import { DEFAULT_PAGESIZE_S, NARRATIVE_EVENT_TYPES } from "../../config";
import { GET_PROCESS_NARRATIVE } from "./queries";

const styles = {
    trGroup: css({
        " .-odd": {
            backgroundColor: "#fafafa",
        },
    }),
    td: css({
        whiteSpace: "normal !important",
    }),
    thead: css({
        backgroundColor: "#666666",
        color: "#ffffffbf",
    }),
};

const tasksWithDocsFilter = taskId => document => {
    const taskTag = document.tags.find(tag => tag.includes("task:"));
    const documentTaskId = taskTag && taskTag.replace("task:", "");
    return taskId === +documentTaskId;
};

const getActionName = event =>
    R.path(
        ["jsonSchema_", "properties", "action", "enum", event.action],
        event
    );

export const prepareEvent = event => ({
    ...formatWithJsonSchema({
        jsonSchema: event.jsonSchema_,
        data: event,
    }),
    action: getActionName(event),
    detail: JSON.parse(event.detail),
});

const columns = (schema, processId) => [
    {
        id: "entity",
        Header: "Entity",
        accessor: "task.entity.name",
    },
    {
        id: "task",
        Header: "Task",
        accessor: "taskName",
    },
    {
        id: "source",
        Header: "Source",
        accessor: "detail.source",
    },
    {
        id: "summary",
        Header: "Summary",
        Cell: row => {
            const summaryInfo = row.original.detail;
            if (!summaryInfo) {
                return "–";
            }

            return row.original.action ? (
                <NarrativeSummary
                    record={{
                        action: row.original.action,
                        detail: summaryInfo,
                    }}
                    schema={schema}
                />
            ) : (
                "-"
            );
        },
    },
    {
        id: "docs",
        Header: "Documents Captured",
        Cell: row => {
            if (
                R.includes(row.original.action, [
                    NARRATIVE_EVENT_TYPES.TASK_PROCESSED_SUMMARY,
                    NARRATIVE_EVENT_TYPES.TASK_DISPOSITIONED,
                    NARRATIVE_EVENT_TYPES.TASK_REVIEWED,
                ])
            ) {
                const tags = `process:${processId}&source:flow_app_upload`;
                return (
                    <DocumentListWithFetch
                        queryParams={{ tags }}
                        documentFilter={tasksWithDocsFilter(
                            row.original.taskId
                        )}
                    />
                );
            }
            return "-";
        },
    },
    {
        id: "user",
        Header: "User",
        accessor: "user.username",
    },
    {
        id: "start",
        Header: "Start",
        Cell: row =>
            formatDatetimeString({
                str: row.original.task.started,
                precision: "SECOND",
            }),
    },
    {
        id: "end",
        Header: "End",
        Cell: row =>
            formatDatetimeString({
                str: row.original.task.finished,
                precision: "SECOND",
            }),
    },
];

const addKeys = (e, _id) => ({ ...e, _id });
const compareEventsByDate = (e1, e2) =>
    new Date(e1.task.started).getTime() - new Date(e2.task.started).getTime();

const NarrativeTable = ({ processRelayId, schema }) => {
    const processId = atob(processRelayId).replace("Process:", "");
    return (
        <Query
            query={GET_PROCESS_NARRATIVE}
            variables={{ id: processRelayId }}
            fetchPolicy="cache-and-network"
        >
            {withApolloState(({ data }) => (
                <Table
                    getTrGroupProps={() => ({
                        className: `${styles.trGroup}`,
                    })}
                    getTheadProps={() => ({
                        className: `${styles.thead}`,
                    })}
                    getTdProps={() => ({
                        className: `${styles.td}`,
                    })}
                    columns={columns(schema, processId)}
                    // TODO: fix when events will have unique id, T8848
                    data={data.process.events
                        .map(prepareEvent)
                        .map(addKeys)
                        .sort(compareEventsByDate)}
                    defaultPageSize={
                        data.process.events.length || DEFAULT_PAGESIZE_S
                    }
                />
            ))}
        </Query>
    );
};

export default NarrativeTable;
